/**
 * Outline mode's model (#1393 M2, contract 3): its copy, the library as
 * resources, each destination and the recorded unit -> session -> topic join.
 */
import { a3Copy } from "@/components/assignments/a3-copy";
import type { VisitAddress } from "@/components/ui/box.schema";
import type { MaterialLibraryRead, ResourcePlacementRole } from "@/lib/api/courseMaterials";
import type { ContentWork, CourseContent } from "@/lib/api/courseContent";
import { joinMaterials } from "../materials/material-facts";

export const OUTLINE_COPY = {
  forbidden: "The course outline isn't available to you.",
  failed: "The course outline couldn't be read.",
  stale: "The course outline couldn't be refreshed. These are the last confirmed topics.",
  noTopics: "This course has no topics yet.",
  looseTopic: "Not in a topic",
  noReadings: "This course has no readings yet.",
  resourcesFailed: "Resources couldn't be read. Topics and readings are current.",
  resourcesStale: "Resources couldn't be refreshed. These are the last confirmed resources.",
  noResources: "This course has no resources yet.",
  notPlaced: "Not placed in a session",
  noGuideTeaching: "No guide has been prepared.",
  noGuideLearner: "Your teachers publish the guide when it is ready.",
} as const;

/** A syllabus or course resource as either seat reads it. */
export interface OutlineResource {
  id: string;
  /** The material record, which a stored `material=M` address names (R16, R48). */
  materialId: string | null;
  title: string;
  role: ResourcePlacementRole;
  unitId: string | null;
  readingId: string | null;
  kind: string;
  published: boolean;
}

/** The library as resources: the editor's joined materials, or the learner's server-narrowed placements. */
export function outlineResources(library: MaterialLibraryRead, teaching: boolean): OutlineResource[] {
  if (teaching) {
    return joinMaterials(library.materials, library.placements).map((item) => ({
      id: item.placement?.id ?? item.material.id,
      materialId: item.material.id,
      title: item.material.title,
      role: item.role,
      unitId: item.material.unit_id,
      readingId: item.material.reading_id ?? item.placement?.reading_id ?? null,
      kind: item.material.material_type ?? (item.material.google_drive_file_id ? a3Copy.googleDrive : a3Copy.link),
      published: item.published,
    }));
  }
  return library.placements.map((placement) => ({
    id: placement.id,
    materialId: placement.material_id,
    title: placement.title ?? a3Copy.courseReference,
    role: placement.placement_role,
    unitId: placement.unit_id ?? null,
    readingId: placement.reading_id ?? null,
    kind: placement.material_type ?? (placement.google_drive_mime_type ? a3Copy.googleDrive : a3Copy.link),
    published: placement.publication_status === "published",
  }));
}

/** A resource's one destination: a reading-backed material opens its reading directly (contract 3). */
export function resourceVisit(courseId: string, resource: Pick<OutlineResource, "id" | "readingId">): VisitAddress {
  return resource.readingId ? `reading:${courseId}/${resource.readingId}` : `material:${courseId}/${resource.id}`;
}

/** A coursework link's destination: a saved teaching draft visits its draft, published work opens in Coursework. */
export function workDestination(courseId: string, courseHref: string, work: ContentWork): { visit: VisitAddress } | { to: string } {
  return work.saved_draft ? { visit: `teaching-draft:${courseId}/${work.id}` } : { to: `${courseHref}?view=coursework&assignment=${encodeURIComponent(work.id)}` };
}

/** The recorded joins: unit -> session -> topic. Only what the content read records places anything. */
export function topicOfUnit(content: CourseContent): (unitId: string | null) => string | null {
  const sessionTopic = new Map(content.sessions.map((session) => [session.id, session.topic_id]));
  const unitTopic = new Map(content.guidance.map((unit) => [unit.id, unit.session_id ? sessionTopic.get(unit.session_id) ?? null : null]));
  return (unitId) => (unitId ? unitTopic.get(unitId) ?? null : null);
}
